/**
 * "Peer review has been requested" line in the report Impression section.
 *
 * Radical (R4V host) posts RADICAL_PEER_REVIEW into the reporting iframe when the
 * user requests / cancels a peer review on the study. We then insert (idempotently)
 * or remove a bold first line under the IMPRESSION heading. The line is ordinary,
 * editable report content — no peer review is created anywhere from here.
 *
 * Works on the TipTap editor (ProseMirror doc). Only top-level blocks are scanned:
 * report headings and the peer review paragraph live at the document root.
 */

export const PEER_REVIEW_LINE_TEXT = "Peer review has been requested";
export const PEER_REVIEW_LINE_HTML = `<p><strong>${PEER_REVIEW_LINE_TEXT}</strong></p>`;

/** Impression section titles (normalized: lowercase, no trailing colon/punctuation). */
const IMPRESSION_HEADING_NAMES = new Set([
  "impression",
  "impressions",
  "conclusion",
  "conclusions",
  "סיכום",
  "התרשמות",
  "الانطباع",
  "الخلاصة",
  "impresión",
  "impresion",
  "conclusión",
  "conclusiones",
]);

function normalizeLabel(text) {
  return String(text || "")
    .replace(/[‎‏]/g, "")
    .trim()
    .replace(/[\s:：.\-–—]+$/u, "")
    .replace(/^[\s\-–—]+/u, "")
    .replace(/\s+/g, " ")
    .toLowerCase();
}

/** @param {string} text */
export function isImpressionHeadingText(text) {
  return IMPRESSION_HEADING_NAMES.has(normalizeLabel(text));
}

/** @param {string} text */
export function isPeerReviewLineText(text) {
  return normalizeLabel(text) === normalizeLabel(PEER_REVIEW_LINE_TEXT);
}

/** Paragraph that is only a section label, e.g. "FINDINGS:" (all caps, ends with colon). */
function isLabelParagraph(node) {
  if (node?.type?.name !== "paragraph") return false;
  const text = String(node.textContent || "").trim();
  return /[:：]$/.test(text) && text.length <= 60 && text === text.toUpperCase();
}

function topLevelBlocks(doc) {
  const blocks = [];
  doc.forEach((node, offset) => {
    blocks.push({ node, pos: offset, end: offset + node.nodeSize });
  });
  return blocks;
}

/**
 * Locate the Impression section.
 * @param {import('prosemirror-model').Node} doc
 * @returns {{ headingIndex: number, insertPos: number, scopeEnd: number, blocks: Array } | null}
 */
export function findImpressionSection(doc) {
  if (!doc || typeof doc.forEach !== "function") return null;
  const blocks = topLevelBlocks(doc);

  const headingIndex = blocks.findIndex(
    ({ node }) =>
      (node.type?.name === "heading" || isLabelParagraph(node)) &&
      isImpressionHeadingText(node.textContent),
  );
  if (headingIndex === -1) return null;

  const heading = blocks[headingIndex].node;
  const isHeadingNode = heading.type?.name === "heading";
  const level = heading.attrs?.level || 1;

  let scopeEnd = doc.content.size;
  for (let i = headingIndex + 1; i < blocks.length; i++) {
    const { node, pos } = blocks[i];
    const endsSection =
      (node.type?.name === "heading" &&
        (!isHeadingNode || (node.attrs?.level || 1) <= level)) ||
      (!isHeadingNode && isLabelParagraph(node));
    if (endsSection) {
      scopeEnd = pos;
      break;
    }
  }

  return {
    headingIndex,
    insertPos: blocks[headingIndex].end,
    scopeEnd,
    blocks,
  };
}

/**
 * Top-level peer review paragraphs within [from, to).
 * @returns {Array<{ pos: number, end: number }>}
 */
function findPeerReviewParagraphs(blocks, from, to) {
  return blocks
    .filter(
      ({ node, pos }) =>
        pos >= from &&
        pos < to &&
        node.type?.name === "paragraph" &&
        isPeerReviewLineText(node.textContent),
    )
    .map(({ pos, end }) => ({ pos, end }));
}

/**
 * Apply the Radical peer review state to the editor.
 *
 * @param {object} editor - TipTap editor
 * @param {boolean} requested - true = ensure line exists; false = remove it
 * @returns {{ ok: boolean, changed: boolean, reason?: "no_editor" | "no_impression" }}
 */
export function applyPeerReviewLine(editor, requested) {
  const doc = editor?.state?.doc;
  if (!doc || typeof editor.chain !== "function") {
    return { ok: false, changed: false, reason: "no_editor" };
  }

  const section = findImpressionSection(doc);

  if (requested) {
    if (!section) return { ok: false, changed: false, reason: "no_impression" };
    const existing = findPeerReviewParagraphs(
      section.blocks,
      section.insertPos,
      section.scopeEnd,
    );
    if (existing.length > 0) return { ok: true, changed: false };
    editor
      .chain()
      .insertContentAt(section.insertPos, PEER_REVIEW_LINE_HTML, {
        updateSelection: false,
      })
      .run();
    return { ok: true, changed: true };
  }

  // Cancel: remove from the Impression section; fall back to the whole doc if the
  // heading was renamed/removed after the line was inserted.
  const blocks = section ? section.blocks : topLevelBlocks(doc);
  const lines = section
    ? findPeerReviewParagraphs(blocks, section.insertPos, section.scopeEnd)
    : findPeerReviewParagraphs(blocks, 0, doc.content.size);
  if (lines.length === 0) return { ok: true, changed: false };

  let chain = editor.chain();
  // Delete from the end so earlier positions stay valid.
  [...lines]
    .sort((a, b) => b.pos - a.pos)
    .forEach(({ pos, end }) => {
      chain = chain.deleteRange({ from: pos, to: end });
    });
  chain.run();
  return { ok: true, changed: true };
}

/**
 * True when a transaction replaced the whole report with different content
 * (Process AI result, template load, draft restore) — i.e. a "new" Impression may
 * have appeared even though the section never disappeared in between.
 * Re-applying identical content (editor ↔ state sync) does not count.
 *
 * @param {{ docChanged?: boolean, before?: object, doc?: object, steps?: Array }} tr
 * @returns {boolean}
 */
export function isFullContentReplacement(tr) {
  if (!tr?.docChanged || !tr.before || !tr.doc || !Array.isArray(tr.steps)) {
    return false;
  }
  const beforeSize = tr.before.content?.size;
  const replacesAll = tr.steps.some(
    (step) => step?.from === 0 && step?.to === beforeSize,
  );
  if (!replacesAll) return false;
  return tr.before.textContent !== tr.doc.textContent;
}

/** @param {object} editor */
export function hasImpressionSection(editor) {
  return Boolean(findImpressionSection(editor?.state?.doc));
}
